<script lang="ts">
/** Cache for async components to prevent remounting on every form update */
const asyncComponentCache = new Map<string, Component>();

export default {
    inheritAttrs: false
};
</script>

<script setup lang="ts">
import { computed, defineAsyncComponent, toRef, type Component } from 'vue';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card/index';

import { getFormData, getNestedValue, useFieldVisibility } from '../../composables/useFieldVisibility';
import { getBlockComponent, hasBlockComponent } from '../../services/componentRegistry';
import type { BlockConfig } from '../../types';

import FieldConfigComponent from './FieldConfig.vue';

interface AutoGenerateState {
    hasAutoGenerate: (key: string) => boolean;
    isManuallyEdited: (key: string) => boolean;
    markManualEdit: (key: string) => void;
    resetAutoGenerate: (key: string) => void;
}

const props = defineProps<{
    block: BlockConfig;
    form: any;
    errors: Record<string, string>;
    trField: (key: string) => string;
    trBlock: (key: string) => string;
    translationPrefix?: string | null;
    /** Optional: The full item being edited, passed to relation fields for preloading labels */
    item?: Record<string, unknown>;
    /** Model name for unique validation API calls */
    model?: string;
    /** Item ID for ignoring current record in unique checks (edit mode) */
    itemId?: string | number | null;
    /** Auto-generate state from parent (optional) */
    autoGenerate?: AutoGenerateState;
}>();

const emit = defineEmits<{
    (e: 'update:field', key: string, value: any): void;
}>();

// Create reactive refs for field visibility
const fieldsRef = computed(() => props.block.fields ?? []);
const formRef = toRef(props, 'form');

// Use the visibility composable
const { isFieldVisible } = useFieldVisibility(fieldsRef, formRef);

/** Get field value supporting dot notation (e.g., 'data.start_date') */
function getFieldValue(key: string): unknown {
    // Use getFormData to handle Inertia form's .data() method conflict
    const formData = getFormData(props.form as Record<string, unknown>);
    return getNestedValue(formData, key);
}

/**
 * Coerce truthiness for `metadata.implies_true` propagation.
 *
 * Switch fields default to plain booleans, but custom trueValue/falseValue
 * pairs are also supported — match the field's configured trueValue when
 * present so the implication fires on the same toggle the user sees.
 */
function isFieldTruthy(value: unknown, trueValue: unknown): boolean {
    if (trueValue !== undefined && trueValue !== null) {
        return value === trueValue;
    }
    return value === true || value === 1 || value === '1' || value === 'true';
}

/** Handle field update supporting dot notation */
function handleFieldUpdate(key: string, value: unknown): void {
    setFormValue(key, value);
    emit('update:field', key, value);

    // Propagate `metadata.implies_true`: when this field's value becomes truthy,
    // coerce each implied field key to true and bubble that update up too.
    const field = (props.block.fields ?? []).find((f) => f.key === key);
    const implies = (field?.metadata as Record<string, unknown> | undefined)?.implies_true;
    if (Array.isArray(implies) && isFieldTruthy(value, field?.trueValue)) {
        for (const impliedKey of implies) {
            if (typeof impliedKey !== 'string') continue;
            const impliedField = (props.block.fields ?? []).find((f) => f.key === impliedKey);
            const impliedTrue = impliedField?.trueValue ?? true;
            setFormValue(impliedKey, impliedTrue);
            emit('update:field', impliedKey, impliedTrue);
        }
    }
}

/** Apply a value to the form, supporting dot-notation paths. */
function setFormValue(key: string, value: unknown): void {
    // Split the key into parts
    const keys = key.split('.');

    if (keys.length === 1) {
        // Simple assignment for top-level keys
        (props.form as any)[key] = value;
    } else {
        // Nested assignment - handle the case where 'data' is actually an Inertia method
        // For nested paths like 'data.start_date', we need to set on the form directly
        // but handle the case where 'data' is actually an Inertia method
        const firstKey = keys[0];

        if (firstKey === 'data' && typeof (props.form as any).data === 'function') {
            // Special case: field key starts with 'data' but form.data is Inertia's method
            // We need to set on the actual data object, not replace the method
            // Access the nested property within the form's reactive structure
            if (keys.length === 2) {
                // Simple case: data.someField - set directly on form
                (props.form as any)[keys[1]] = value;
            } else {
                // Complex case: data.nested.field - navigate through form properties
                let target: any = props.form;
                for (let i = 1; i < keys.length - 1; i++) {
                    const k = keys[i];
                    if (target[k] === undefined || target[k] === null || typeof target[k] !== 'object') {
                        target[k] = {};
                    }
                    target = target[k];
                }
                target[keys[keys.length - 1]] = value;
            }
        } else {
            // Standard nested path - navigate through form
            let target: any = props.form;

            for (let i = 0; i < keys.length - 1; i++) {
                const k = keys[i];
                let next = target[k];

                // Skip Inertia's built-in methods when navigating
                if (typeof next === 'function' && ['data', 'transform', 'defaults', 'reset', 'clearErrors', 'setError', 'errors', 'hasErrors', 'processing', 'progress', 'wasSuccessful', 'recentlySuccessful', 'submit', 'get', 'post', 'put', 'patch', 'delete', 'cancel'].includes(k)) {
                    // This key conflicts with an Inertia method - initialize as object
                    target[k] = {};
                    next = target[k];
                } else if (next === undefined || next === null || typeof next !== 'object') {
                    target[k] = {};
                    next = target[k];
                }

                target = next;
            }

            // Set the final value
            const finalKey = keys[keys.length - 1];
            target[finalKey] = value;
        }
    }
}

// ============================================================================
// Custom Component Support
// ============================================================================

/** Check if block has a custom component */
const hasCustomComponent = computed(() => {
    return !!props.block.component && hasBlockComponent(props.block.component);
});

/** Get the custom block component if registered */
const customBlockComponent = computed<Component | null>(() => {
    const componentName = props.block.component;
    if (!componentName) return null;

    console.log(`[BlockConfig] Resolving custom component: ${componentName}`);

    // Return from cache if already resolved
    if (asyncComponentCache.has(componentName)) {
        return asyncComponentCache.get(componentName)!;
    }

    const component = getBlockComponent(componentName);
    if (!component) {
        console.warn(`[BlockConfig] Custom component NOT FOUND: ${componentName}`);
        return null;
    }

    console.log(`[BlockConfig] Found custom component: ${componentName}`, component);
    if (typeof component === 'function') {
        const asyncComp = defineAsyncComponent(component as () => Promise<{ default: Component }>);
        asyncComponentCache.set(componentName, asyncComp);
        return asyncComp;
    }

    // If it's already a component module with default export
    const resolvedComp = (component as { default: Component }).default ?? null;
    if (resolvedComp) {
        asyncComponentCache.set(componentName, resolvedComp);
    }
    return resolvedComp;
});
</script>

<template>
    <!-- Block Breakpoint -->
    <div v-if="block.isBreakpoint" class="col-span-full h-0 opacity-0"></div>

    <!-- Custom Block Component -->
    <component
        v-else-if="hasCustomComponent && customBlockComponent"
        :is="customBlockComponent"
        :block="block"
        :form="form"
        :errors="errors"
        :tr-field="trField"
        :tr-block="trBlock"
        :translation-prefix="translationPrefix"
        :item="item"
        :model="model"
        :item-id="itemId"
        :component-props="block.componentProps"
        @update:field="handleFieldUpdate"
    />

    <!-- Unregistered Custom Component Warning -->
    <Card v-else-if="block.component && !hasCustomComponent" class="border-destructive/50 bg-destructive/5">
        <CardHeader>
            <CardTitle class="text-destructive text-lg">{{ trBlock(block.key) }}</CardTitle>
            <CardDescription class="text-destructive/80">
                Custom component "{{ block.component }}" is not registered.
            </CardDescription>
        </CardHeader>
    </Card>

    <!-- Standard Block -->
    <Card v-else class="@container/block">
        <CardHeader>
            <CardTitle class="text-lg">{{ trBlock(block.key) }}</CardTitle>
            <CardDescription v-if="block.description">{{ block.description }}</CardDescription>
        </CardHeader>
        <CardContent>
            <div class="@[400px]/block:grid-cols-2 @[700px]/block:grid-cols-3 grid grid-cols-1 gap-6">
                <FieldConfigComponent
                    v-for="field in block.fields"
                    v-show="isFieldVisible(field)"
                    :key="field.key"
                    :field="field"
                    :model-value="getFieldValue(field.key)"
                    :error="errors[field.key]"
                    :tr-field="trField"
                    :translation-prefix="translationPrefix"
                    :item="item"
                    :model="model"
                    :item-id="itemId"
                    :is-auto-generating="autoGenerate?.hasAutoGenerate(field.key)"
                    :is-manually-edited="autoGenerate?.isManuallyEdited(field.key)"
                    @update:model-value="handleFieldUpdate(field.key, $event)"
                    @manual-edit="(key: string) => autoGenerate?.markManualEdit(key)"
                    @reset-auto-generate="(key: string) => autoGenerate?.resetAutoGenerate(key)"
                />
            </div>
        </CardContent>
    </Card>
</template>
